import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, ApiError, type AcessoEstado } from '../../api';
import { idiomaAtual, t } from '../../i18n/idioma';
import { useIdioma } from '../../i18n/useIdioma';
import { montarAntiRobo, type AntiRobo } from './antiRobo';
import { Cadeado, Carta, Seta } from './icones';
import { origemDaUrl, redesDe } from './links';

type Etapa = 'form' | 'enviando' | 'ok';
type Campo = 'email' | 'aceite' | null;

const EMAIL_MIN = 5;
const EMAIL_MAX = 254;

/** Mesma regra da tela do protótipo; quem decide de verdade é o servidor. */
function emailValido(bruto: string): boolean {
  const e = bruto.trim();
  if (e.length < EMAIL_MIN || e.length > EMAIL_MAX || /\s/.test(e)) return false;
  const at = e.indexOf('@');
  return at >= 1 && at === e.lastIndexOf('@') && at !== e.length - 1 && e.slice(at + 1).includes('.');
}

/** Só para a tela: `v***@example.com`. Não vai a log. */
function mascarar(email: string): string {
  const [local, dominio] = email.split('@');
  if (!dominio || !local) return '***';
  return `${local[0]}${'*'.repeat(Math.max(3, Math.min(6, local.length - 1)))}@${dominio}`;
}

/** O cartão do pré-registro (techspec-landing-fase §06.3). */
export function PreRegistroCard({ estado, founders = false, onLogin }: {
  estado: AcessoEstado;
  founders?: boolean;
  /** O cartão vira o login (Entrar ou Convite) na própria landing (techspec-landing-fase §06.7). */
  onLogin?(aba: 'entrar' | 'convite'): void;
}) {
  const idioma = useIdioma();
  const [etapa, setEtapa] = useState<Etapa>('form');
  const [fechou, setFechou] = useState(false);
  const [email, setEmail] = useState('');
  const [aceite, setAceite] = useState(false);
  const [site, setSite] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [campo, setCampo] = useState<Campo>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const aceiteRef = useRef<HTMLInputElement>(null);
  const caixaAntiRobo = useRef<HTMLDivElement>(null);
  const antiRobo = useRef<Promise<AntiRobo> | null>(null);
  const chaveSite = estado.antiRobo?.chaveSite ?? null;
  const fechado = fechou || !estado.inscricoesAbertas;

  useEffect(() => {
    const el = caixaAntiRobo.current;
    if (!chaveSite || !el || fechado || etapa === 'ok') return;
    const montado = montarAntiRobo(el, chaveSite, idioma);
    montado.catch(() => {});
    antiRobo.current = montado;
    return () => {
      antiRobo.current = null;
      void montado.then(w => w.remover()).catch(() => {});
    };
  }, [chaveSite, idioma, fechado, etapa === 'ok']);

  useEffect(() => {
    if (etapa === 'form' && campo === 'email') emailRef.current?.focus();
    if (etapa === 'form' && campo === 'aceite') aceiteRef.current?.focus();
  }, [campo, etapa]);

  const falhar = (mensagem: string, qual: Campo = null) => {
    setErro(mensagem);
    setCampo(qual);
    setEtapa('form');
  };

  const tokenAntiRobo = async (): Promise<string | undefined> => {
    if (!chaveSite) return undefined;
    const widget = await antiRobo.current;
    if (!widget) throw new Error('antiRobo');
    return widget.obterToken();
  };

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    if (etapa === 'enviando') return;
    setErro(null);
    setCampo(null);
    if (!emailValido(email)) return falhar(t('landing.form.erroEmail'), 'email');
    if (!aceite) return falhar(t('landing.form.erroAceite'), 'aceite');
    setEtapa('enviando');

    let token: string | undefined;
    try {
      token = await tokenAntiRobo();
    } catch {
      return falhar(t('landing.form.erroAntiRobo'));
    }

    try {
      await api.inscreverAcesso({
        email: email.trim(),
        idioma: idiomaAtual(),
        origem: origemDaUrl(),
        aceite,
        site,
        antiRobo: token
      });
      setEmail(email.trim());
      setEtapa('ok');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'acesso.jaInscrito') {
        falhar(err.message, 'email');
      } else if (err instanceof ApiError && err.code === 'acesso.inscricoesFechadas') {
        setFechou(true);
        setEtapa('form');
      } else if (err instanceof ApiError && err.status === 429) {
        falhar(t('landing.form.erroLimite'));
      } else if (err instanceof ApiError && err.code === 'acesso.antiRobo') {
        falhar(t('landing.form.erroAntiRobo'));
      } else if (err instanceof ApiError && err.status === 400) {
        falhar(err.message, 'email');
      } else {
        falhar(t('landing.form.erroRede'));
      }
    } finally {
      void antiRobo.current?.then(w => w.reiniciar()).catch(() => {});
    }
  };

  const usarOutro = () => {
    setEmail('');
    setAceite(false);
    setErro(null);
    setCampo('email');
    setEtapa('form');
  };

  const pe = onLogin ? (
    <div className="lp-gump__pe">
      {t('landing.form.convite')}{' '}
      <button type="button" className="lp-elo" onClick={() => onLogin('convite')}>{t('landing.form.conviteLink')}</button>
      <br />
      {t('landing.aberta.jaTem')}{' '}
      <button type="button" className="lp-elo" onClick={() => onLogin('entrar')}>{t('entrada.abaEntrar')}</button>
    </div>
  ) : (
    <div className="lp-gump__pe">
      {t('landing.form.convite')} <a href="/convite">{t('landing.form.conviteLink')}</a>
    </div>
  );

  if (fechado) {
    return (
      <>
        <div className="lp-gump__cab">
          <h2>{t('landing.fechado.titulo')}</h2>
          <p>{t('landing.fechado.sub')}</p>
        </div>
        <div className="lp-gump__corpo">
          <div className="lp-ok">
            <div className="lp-ok__icone"><Cadeado /></div>
            <p className="lp-miudo">{t('landing.fechado.texto')}</p>
            <a className="lp-btn" href={redesDe(estado.redes).discord} target="_blank" rel="noopener noreferrer">{t('landing.fechado.discord')}</a>
          </div>
        </div>
        {pe}
      </>
    );
  }

  if (etapa === 'ok') {
    const [antes, depois] = t('landing.ok.texto', { email: '\u0000' }).split('\u0000');
    return (
      <>
        <div className="lp-gump__cab">
          <h2>{t('landing.ok.titulo')}</h2>
          <p>{t('landing.ok.sub')}</p>
        </div>
        <div className="lp-gump__corpo">
          <div className="lp-ok" role="status">
            <div className="lp-ok__icone"><Carta /></div>
            <p>{antes}<b>{mascarar(email)}</b>{depois}</p>
            <p className="lp-miudo">{t('landing.ok.miudo')}</p>
            <button type="button" className="lp-btn" onClick={usarOutro}>{t('landing.ok.outro')}</button>
            {founders && <a className="lp-f-ok" href="#fundadores">{t('landing.fund.okLink')}</a>}
          </div>
        </div>
      </>
    );
  }

  const enviando = etapa === 'enviando';
  return (
    <>
      <div className="lp-gump__cab">
        <h2>{t('landing.form.titulo')}</h2>
        <p>{t('landing.form.sub')}</p>
      </div>
      <form className="lp-gump__corpo" onSubmit={e => { void enviar(e); }} noValidate>
        <div className={campo === 'email' ? 'lp-campo is-ruim' : 'lp-campo'}>
          <label htmlFor="lp-email">{t('landing.form.email')}</label>
          <input
            ref={emailRef}
            id="lp-email"
            name="email"
            type="email"
            value={email}
            placeholder={t('landing.form.emailExemplo')}
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            maxLength={EMAIL_MAX}
            disabled={enviando}
            aria-invalid={campo === 'email' || undefined}
            aria-describedby={erro ? 'lp-erro' : undefined}
            onChange={e => { setEmail(e.target.value); if (campo === 'email') setCampo(null); }}
          />
        </div>
        <div className="lp-isca" aria-hidden="true">
          <label htmlFor="lp-site">{t('landing.form.isca')}</label>
          <input id="lp-site" name="site" tabIndex={-1} autoComplete="off" value={site} onChange={e => setSite(e.target.value)} />
        </div>
        <label className={campo === 'aceite' ? 'lp-aceite is-ruim' : 'lp-aceite'}>
          <input
            ref={aceiteRef}
            type="checkbox"
            checked={aceite}
            disabled={enviando}
            aria-invalid={campo === 'aceite' || undefined}
            onChange={e => { setAceite(e.target.checked); if (campo === 'aceite') setCampo(null); }}
          />
          <span>{t('landing.form.aceite')}</span>
        </label>
        {erro && <p className="lp-aviso" id="lp-erro" role="alert">{erro}</p>}
        <button type="submit" className="lp-btn lp-btn--ouro" disabled={enviando}>
          {enviando
            ? <><span className="lp-carregando" aria-hidden="true" /> {t('landing.form.enviando')}</>
            : <>{t('landing.form.enviar')} <Seta /></>}
        </button>
        <p className="lp-miudo">{t('landing.form.miudo')}</p>
      </form>
      <div className="lp-antirobo" ref={caixaAntiRobo} />
      {pe}
    </>
  );
}
